import { createTheme } from '@mui/material/styles';
import { clarityTokens, generateColor, generateOpacityColor } from '@verifime/design-tokens';
import {
  commonComponentsOverride,
  identityCard,
  paletteGrey,
  requestActions,
  requestBanner,
  requestCard,
  requestDialog,
  semanticBorderRadius,
  semanticFontWeight,
  semanticFontSize,
  semanticLetterSpacing,
  semanticLineHeight,
  semanticSpacing,
  semanticSize,
  typographyTheme,
} from './common';

const theme = createTheme({
  semantic: {
    borderRadius: semanticBorderRadius,
    fontWeight: semanticFontWeight,
    fontSize: semanticFontSize,
    letterSpacing: semanticLetterSpacing,
    lineHeight: semanticLineHeight,
    spacing: semanticSpacing,
    size: semanticSize,
  },
  logo: {
    width: semanticSize['2xs'],
    padding: semanticSpacing['2xs'],
    borderRadius: semanticSpacing['2xs'],
  },
  card: {
    padding: semanticSpacing.base,
    borderRadius: semanticBorderRadius.sm,
    borderWidth: semanticSpacing['4xs'],
    contentGap: semanticSpacing.sm,
    itemGap: semanticSpacing.sm,
  },
  // Hover and press colours of the request-screen identity cards (the DS card.background.hover,
  // card.border.hover and card.background.active tones).
  identityCard: {
    ...identityCard,
    hoverBackground: clarityTokens.colorEggplant050,
    hoverBorder: clarityTokens.colorEggplant100,
    activeBackground: clarityTokens.colorEggplant100,
  },
  requestActions,
  // The board's default border colour for a dot, and the tint behind a back or next button under
  // the pointer (the DS subtle active primary tone).
  requestBanner: {
    ...requestBanner,
    dotColor: clarityTokens.colorGrey200,
    stepHoverBackground: clarityTokens.colorEggplant100,
  },
  requestCard,
  requestDialog,
  notice: {
    info: {
      background: clarityTokens.colorBlue050,
      foreground: clarityTokens.colorBlue600,
    },
    danger: {
      background: clarityTokens.colorRed050,
      foreground: clarityTokens.colorRed500,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: generateColor(clarityTokens.brandColorPrimary, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorPrimary, '600', 'clarity'),
      light: generateColor(clarityTokens.brandColorPrimary, '400', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    secondary: {
      main: generateColor(clarityTokens.brandColorSecondary, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorSecondary, '600', 'clarity'),
      light: generateColor(clarityTokens.brandColorSecondary, '400', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    error: {
      main: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorError, '600', 'clarity'),
      light: generateColor(clarityTokens.brandColorError, '400', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    warning: {
      main: generateColor(clarityTokens.brandColorWarning, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorWarning, '600', 'clarity'),
      light: generateColor(clarityTokens.brandColorWarning, '400', 'clarity'),
      contrastText: clarityTokens.colorBlack,
    },
    info: {
      main: generateColor(clarityTokens.brandColorInfo, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorInfo, '600', 'clarity'),
      light: generateColor(clarityTokens.brandColorInfo, '400', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    success: {
      main: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorSuccess, '600', 'clarity'),
      light: generateColor(clarityTokens.brandColorSuccess, '400', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    text: {
      primary: clarityTokens.colorBlack,
      secondary: clarityTokens.colorBlack,
      disabled: clarityTokens.colorGrey400,
      permanentBlack: clarityTokens.colorBlack,
      permanentWhite: clarityTokens.colorWhite,
      tertiary: clarityTokens.colorGrey500,
      success: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
      danger: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
    },
    // Holds Eggplant 500 in both themes, the same numbered gradation repeated rather than a
    // dedicated ramp: see risk.high.fill below for the precedent, and Palette.permanent for why.
    permanent: {
      primary: generateColor(clarityTokens.brandColorPrimary, '500', 'clarity'),
      // Same reasoning as primary above: StatusKnockoutTile's failure states must read as the
      // same red regardless of light/dark mode (C4), so this repeats Red 500 in both theme
      // files rather than tracking mode the way palette.error.main does.
      danger: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
    },
    background: {
      default: clarityTokens.colorGrey020,
      paper: clarityTokens.colorWhite,
    },
    action: {
      disabled: clarityTokens.colorGrey400,
      disabledBackground: clarityTokens.colorGrey200,
    },
    surface: {
      logo: clarityTokens.colorWhite,
      brandSubtle: generateColor(clarityTokens.brandColorPrimary, '050', 'clarity'),
      successSubtle: generateColor(clarityTokens.brandColorSuccess, '050', 'clarity'),
      dangerSubtle: generateColor(clarityTokens.brandColorError, '050', 'clarity'),
      warningSubtle: generateColor(clarityTokens.brandColorWarning, '050', 'clarity'),
      neutralSubtle: clarityTokens.colorGrey050,
      // Same primitive as neutralSubtle: the artboard's light treatment for a raised neutral
      // panel is identical to its plain neutral-subtle one, so there is nothing to hold apart.
      neutralSubtleRaised: clarityTokens.colorGrey050,
    },
    border: {
      decorative: clarityTokens.colorGrey500,
      subtleAccent: generateColor(clarityTokens.brandColorPrimary, '200', 'clarity'),
    },
    status: {
      success: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      warning: {
        text: clarityTokens.colorBlack,
        fill: generateColor(clarityTokens.brandColorWarning, '600', 'clarity'),
        icon: clarityTokens.colorBlack,
      },
      error: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      info: {
        text: generateColor(clarityTokens.brandColorInfo, '600', 'clarity'),
        border: generateColor(clarityTokens.brandColorInfo, '600', 'clarity'),
      },
    },
    interactive: {
      disabled: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey400,
        icon: clarityTokens.colorWhite,
      },
      enabled: {
        text: clarityTokens.colorBlack,
        fill: clarityTokens.colorBlack,
        icon: generateOpacityColor(clarityTokens.colorBlack, 0.5),
      },
    },
    risk: {
      extreme: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey800,
        border: clarityTokens.colorBlack,
        icon: clarityTokens.colorWhite,
      },
      high: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      medium: {
        text: clarityTokens.colorBlack,
        fill: generateColor(clarityTokens.brandColorWarning, '600', 'clarity'),
        icon: clarityTokens.colorBlack,
      },
      low: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      pending: {
        text: clarityTokens.colorGrey400,
        fill: clarityTokens.colorWhite,
        border: clarityTokens.colorGrey400,
        icon: generateOpacityColor(clarityTokens.colorWhite, 0.5),
      },
    },
    confidence: {
      none: {
        text: clarityTokens.colorGrey600,
        fill: clarityTokens.colorGrey100,
      },
      pending: {
        text: clarityTokens.colorGrey600,
        fill: clarityTokens.colorGrey200,
      },
      low: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
      },
      substantial: {
        text: clarityTokens.colorBlack,
        fill: generateColor(clarityTokens.brandColorWarning, '500', 'clarity'),
      },
      high: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
      },
      /**
       * The "what do you do now" box beneath a card's statement. Deliberately not one of the tone
       * colours above: `ConfidenceGuidance` renders on every axis outcome that carries a next
       * step, independent of which tone the meter above it is showing, so it takes its own pair
       * from the brand primary ramp rather than borrowing a tone that would tie its appearance to
       * a specific outcome.
       */
      guidance: {
        fill: generateColor(clarityTokens.brandColorPrimary, '050', 'clarity'),
        text: generateColor(clarityTokens.brandColorPrimary, '800', 'clarity'),
      },
    },
    // Identical across all four themes on purpose: these sit over a video frame, not over
    // the page surface, so they take their contrast from the capture rather than the theme.
    mediaOverlay: {
      scrim: generateOpacityColor(clarityTokens.colorBlack, 0.72),
      control: generateOpacityColor(clarityTokens.colorWhite, 0.22),
      controlHover: generateOpacityColor(clarityTokens.colorWhite, 0.34),
      track: generateOpacityColor(clarityTokens.colorWhite, 0.28),
      halo: generateOpacityColor(clarityTokens.colorWhite, 0.34),
    },
    entity: {
      verified: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      rejected: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      draft: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey400,
        icon: generateOpacityColor(clarityTokens.colorWhite, 0.5),
      },
      inProgress: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey400,
        icon: clarityTokens.colorWhite,
      },
      empty: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey400,
        icon: clarityTokens.colorWhite,
      },
    },
    grey: paletteGrey,
  },
  typography: typographyTheme,
  components: {
    ...commonComponentsOverride,
    MuiLink: {
      ...commonComponentsOverride.MuiLink,
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const color = ownerState.color;

          if (!color) {
            return {
              '&:hover': {
                color: generateColor(clarityTokens.brandColorPrimary, '600', 'clarity'),
              },
            };
          }

          // Handle dot notation colors like 'warning.main'
          if (typeof color === 'string' && color.includes('.')) {
            const [paletteColor] = color.split('.');
            const validColors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];

            if (validColors.includes(paletteColor)) {
              return {
                '&:hover': {
                  color:
                    theme.palette[
                      paletteColor as
                        | 'primary'
                        | 'secondary'
                        | 'error'
                        | 'warning'
                        | 'info'
                        | 'success'
                    ].dark,
                },
              };
            }
          }

          // Handle simple color names
          const validColors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];
          if (validColors.includes(color as string)) {
            return {
              '&:hover': {
                color:
                  theme.palette[
                    color as 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
                  ].dark,
              },
            };
          }

          return {
            '&:hover': {
              color: generateColor(clarityTokens.brandColorPrimary, '600', 'clarity'),
            },
          };
        },
      },
    },
  },
});

export default theme;
